import { useMemo, useState } from "react";
import {
  MinusIcon,
  PlusIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

import { formatCurrency } from "helpers/currency";
import ClampedText from "components/ClampedText";

import { useArrangement } from "./ArrangementContext";
import type { AddOnService, AddOnServiceGroup, OfferingMedia } from "./types";
import {
  getIncludedOfferingQuantity,
  getRushCremationVariantId,
} from "./helpers";
import OfferingMediaGalleryModal from "./OfferingMediaGalleryModal";
import OfferingMediaThumbnail from "./OfferingMediaThumbnail";
import { getSortedOfferingMedia } from "./offeringMedia";

type AddOnServicesSectionProps = {
  readOnly?: boolean;
};

type GalleryState = {
  service: AddOnService;
  media: OfferingMedia[];
};

const byPriceAsc = (a: AddOnService, b: AddOnService) =>
  a.pricing.amount - b.pricing.amount;

export default function AddOnServicesSection({
  readOnly = false,
}: AddOnServicesSectionProps) {
  const {
    state,
    setAddOnSelection,
    clearAddOnSelection,
    config,
    packageInclusions,
  } = useArrangement();
  const [gallery, setGallery] = useState<GalleryState | null>(null);

  // Add-ons labelled "test-only" stay hidden unless already selected, so a
  // family who picked one keeps seeing it. Informational-only add-ons can't be
  // selected or priced, so they show only where the selected package includes
  // them; anywhere else their "Included in …" badge would be untrue.
  const visibleAddOns = useMemo(() => {
    const rushVariantId = getRushCremationVariantId(config);
    const addOns = (config?.add_ons ?? []).filter(
      (a) => a.id !== rushVariantId,
    );
    const selectedIds = new Set(state.addOnSelections.map((a) => a.serviceId));
    return addOns.filter(
      (a) =>
        (!a.labels?.includes("test-only") || selectedIds.has(a.id)) &&
        (!a.informationalOnly ||
          getIncludedOfferingQuantity(packageInclusions, a.offeringId) > 0),
    );
  }, [config, state.addOnSelections, packageInclusions]);

  const selectedPkg = config?.packages.find(
    (p) => p.variant_id === state.selectedPackageId,
  );
  const pkgName = selectedPkg?.title || "your plan";

  // Build groups from server-provided metadata (config.add_on_groups) and
  // attach services by matching slug. Groups without services are dropped
  // so empty headings don't render.
  const groups = useMemo<AddOnServiceGroup[]>(() => {
    const metas = [...(config?.add_on_groups ?? [])].sort(
      (a, b) => a.display_order - b.display_order,
    );
    return metas
      .map((meta) => ({
        id: meta.slug,
        title: meta.title,
        subtitle: meta.subtitle,
        exclusive: meta.exclusive,
        services: visibleAddOns
          .filter((addon) => addon.group === meta.slug)
          .sort(byPriceAsc),
      }))
      .filter((group) => group.services.length > 0);
  }, [visibleAddOns, config?.add_on_groups]);

  const individualAddOns = useMemo(
    () => visibleAddOns.filter((s) => !s.group).sort(byPriceAsc),
    [visibleAddOns],
  );

  const getSelection = (serviceId: string) =>
    state.addOnSelections.find((a) => a.serviceId === serviceId);

  const openGallery = (service: AddOnService, media: OfferingMedia[]) => {
    if (!media.length) return;
    setGallery({ service, media });
  };

  // Informational items and per-unit items use IndividualServiceCard:
  // informational (already filtered to the selected package's inclusions)
  // shows the "included" badge with no controls; per-unit shows the quantity
  // stepper. Flat, selectable items use the single select/deselect toggle
  // card. Shared by grouped and standalone add-ons so a flat add-on never
  // renders a misleading stepper and informational items keep their badge.
  const renderService = (service: AddOnService) => {
    const selection = getSelection(service.id);
    const includedQty = getIncludedOfferingQuantity(
      packageInclusions,
      service.offeringId,
    );
    const media = getSortedOfferingMedia(service.media);
    if (service.informationalOnly || service.pricing.type === "PER_UNIT") {
      return (
        <IndividualServiceCard
          key={service.id}
          service={service}
          media={media}
          quantity={selection?.quantity ?? 0}
          includedQty={includedQty}
          pkgName={pkgName}
          readOnly={readOnly}
          onClear={() => clearAddOnSelection(service.id)}
          onQuantityChange={(qty) => setAddOnSelection(service.id, qty)}
          onOpenVideo={() => openGallery(service, media)}
        />
      );
    }
    const isSelected = !!selection;
    return (
      <GroupServiceCard
        key={service.id}
        service={service}
        media={media}
        isSelected={isSelected}
        includedQty={includedQty}
        readOnly={readOnly}
        onToggle={() => {
          if (readOnly) return;
          if (isSelected) {
            clearAddOnSelection(service.id);
          } else {
            setAddOnSelection(service.id, 1);
          }
        }}
        onOpenVideo={() => openGallery(service, media)}
      />
    );
  };

  return (
    <>
      <div className="space-y-10">
        {/* Grouped services */}
        {groups.map((group) => (
          <div key={group.id}>
            <h2 className="font-serif text-2xl italic text-evergreen">
              {group.title}
            </h2>
            {group.subtitle && (
              <p className="mt-1 text-lg text-soil">{group.subtitle}</p>
            )}
            <div className="mt-4 space-y-3">
              {group.services.map(renderService)}
            </div>
          </div>
        ))}

        {/* Individual add-ons */}
        {individualAddOns.length > 0 && (
          <div>
            <h2 className="font-serif text-2xl italic text-evergreen">
              Additional Services
            </h2>
            <p className="mt-1 text-lg text-soil">
              Select any additional services you&apos;d like to add.
            </p>
            <div className="mt-4 space-y-3">
              {individualAddOns.map(renderService)}
            </div>
          </div>
        )}
      </div>

      <OfferingMediaGalleryModal
        media={gallery?.media ?? []}
        serviceName={gallery?.service.name ?? ""}
        isOpen={!!gallery}
        onClose={() => setGallery(null)}
      />
    </>
  );
}

function GroupServiceCard({
  service,
  media,
  isSelected,
  includedQty,
  readOnly,
  onToggle,
  onOpenVideo,
}: {
  service: AddOnService;
  media: OfferingMedia[];
  isSelected: boolean;
  includedQty: number;
  readOnly: boolean;
  onToggle: () => void;
  onOpenVideo: () => void;
}) {
  const isIncluded = includedQty > 0;
  const hasMedia = media.length > 0;

  // role="checkbox" (not <button>) so the description's "Show more" toggle can
  // be a real button without nesting inside another button.
  return (
    <div
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={readOnly}
      aria-label={service.name}
      tabIndex={readOnly ? -1 : 0}
      onClick={() => {
        if (!readOnly) onToggle();
      }}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`group w-full overflow-hidden rounded-md border bg-white text-left transition ${
        isSelected ? "border-evergreen font-bold" : "border-olive"
      } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
    >
      {hasMedia && (
        <div className="border-b border-oat">
          <OfferingMediaThumbnail
            media={media}
            serviceName={service.name}
            onOpenVideo={onOpenVideo}
          />
        </div>
      )}
      <div
        className={`flex items-start p-4 pb-6 ${
          isSelected
            ? "bg-ivory"
            : "[@media(hover:hover)]:group-hover:bg-oat-50"
        }`}
      >
        <div className="mr-4 mt-0.5 shrink-0">
          <div
            className={`h-5 w-5 rounded-full border border-olive ${
              isSelected ? "bg-cantelope" : ""
            }`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <span className="break-words text-lg font-semibold text-evergreen">
              {service.name}
            </span>
            <span className="shrink-0 text-lg font-bold text-evergreen">
              {isIncluded ? "Included" : formatCurrency(service.pricing.amount)}
            </span>
          </div>
          {isIncluded && (
            <div className="mt-1">
              <span className="inline-flex rounded-full bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">
                Included in your plan
              </span>
            </div>
          )}
          <ClampedText className="mt-1 text-base font-normal text-soil">
            {service.description}
          </ClampedText>
          {service.blurbs && service.blurbs.length > 0 && (
            <ul className="mt-2 space-y-1">
              {service.blurbs.map((blurb) => (
                <li key={blurb} className="text-sm font-normal text-soil">
                  &bull; {blurb}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function IndividualServiceCard({
  service,
  media,
  quantity,
  includedQty,
  pkgName,
  readOnly,
  onClear,
  onQuantityChange,
  onOpenVideo,
}: {
  service: AddOnService;
  media: OfferingMedia[];
  quantity: number;
  includedQty: number;
  pkgName: string;
  readOnly: boolean;
  onClear: () => void;
  onQuantityChange: (qty: number) => void;
  onOpenVideo: () => void;
}) {
  const isInformational = service.informationalOnly;
  const isIncluded = includedQty > 0;
  const isPerUnit = service.pricing.type === "PER_UNIT";
  const chargeableQty = Math.max(0, quantity - includedQty);
  const total = service.pricing.amount * chargeableQty;
  const hasMedia = media.length > 0;

  return (
    <div
      className={`overflow-hidden rounded-md border bg-white transition ${
        isInformational
          ? "border-olive"
          : quantity > 0
            ? "border-evergreen"
            : "border-olive"
      }`}
    >
      {hasMedia && (
        <div className="border-b border-oat">
          <OfferingMediaThumbnail
            media={media}
            serviceName={service.name}
            onOpenVideo={onOpenVideo}
          />
        </div>
      )}
      <div
        className={`p-4 ${!isInformational && quantity > 0 ? "bg-ivory" : ""}`}
      >
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <span className="break-words text-lg font-semibold text-evergreen">
            {service.name}
          </span>
          {isIncluded && (
            // Pushed to the right of the title on desktop; wraps to its own
            // full-width line below the title on mobile.
            <div className="order-last w-full sm:order-none sm:ml-auto sm:w-auto">
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-evergreen px-2.5 py-0.5 text-xs font-medium text-white">
                <InformationCircleIcon className="h-3.5 w-3.5 shrink-0" />
                {isIncluded && includedQty > 1
                  ? `${includedQty}x included in ${pkgName}`
                  : `Included in ${pkgName}`}
              </span>
            </div>
          )}
          {!isInformational && (
            <span
              className={`shrink-0 text-lg font-bold text-evergreen ${
                isIncluded ? "ml-auto sm:ml-3" : "ml-auto"
              }`}
            >
              {formatCurrency(service.pricing.amount)}
              {isPerUnit && (
                <span className="text-sm font-normal text-soil">/ea</span>
              )}
            </span>
          )}
        </div>
        <ClampedText className="mt-1 text-base text-soil">
          {service.description}
        </ClampedText>

        {!isInformational && (
          <div className="mt-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  if (quantity <= 0) return;
                  if (isPerUnit) {
                    if (quantity <= 1) onClear();
                    else onQuantityChange(quantity - 1);
                  } else {
                    onClear();
                  }
                }}
                disabled={readOnly || quantity <= 0}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-olive text-evergreen transition hover:bg-oat disabled:cursor-default disabled:opacity-40"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="w-8 text-center text-lg font-bold text-evergreen">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  if (isPerUnit) {
                    onQuantityChange(quantity + 1);
                  } else {
                    if (quantity === 0) onQuantityChange(1);
                  }
                }}
                disabled={readOnly || (!isPerUnit && quantity >= 1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-olive text-evergreen transition hover:bg-oat disabled:cursor-default disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
            {quantity > 0 && (
              <span className="shrink-0 text-lg font-bold text-evergreen">
                {formatCurrency(total)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
